




import { Component, Mixins } from "vue-property-decorator";
import { namespace } from "vuex-class";
import { StoreModule } from "@/store/types";
import { GlobalActions } from "@/store/modules/global/types";
import { UserActions } from "@/store/modules/user/types";
import { User } from "@/models/user/user";
import { Roles } from "@/models/user/roles";
import AuthService, { SSOImpersonation } from "@/services/auth_service";
import { PartialNetSuiteCustomer, PriceLevel } from "@/models/netsuite/netsuite";
import { EventBus } from "@/event-bus";
import { Mobile } from "@/mixins/mobile";
import { OrderActions } from "@/store/modules/order/types";

@Component
export default class AuthCallback extends Mixins(Mobile) {
  @(namespace(StoreModule.Global).Action(GlobalActions.AddAuthToken))
  setAuthToken!: (value: string | null) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddLoggedIn))
  setLoggedIn!: (value: boolean) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddRole))
  setUserRole!: (value: Roles | null) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddCustomers))
  clearCustomers!: (value: PartialNetSuiteCustomer[]) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddPriceLevels))
  clearPriceLevels!: (value: PriceLevel[]) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddImpersonation))
  setImpersonation!: (value: SSOImpersonation | null) => Promise<void>;
  @(namespace(StoreModule.User).Action(UserActions.AddUser))
  setUserInfo!: (value: User) => Promise<void>;
  @(namespace(StoreModule.Global).Action(GlobalActions.AddMultiplier))
  setMultiplier!: (value: PriceLevel | null) => Promise<void>;
  @(namespace(StoreModule.Order).Action(OrderActions.ResetForUserChange))
  resetOrderForUserChange!: () => Promise<void>;

  private authService = new AuthService();
  private loader: any = null;

  /**
   * FUPOF-191 (BIGGER-TWEAKS #2): the order store — including the selected
   * customer — is persisted to localStorage, so it outlives the session. If a
   * different user signs in (normal login or an impersonation takeover), the
   * previous user's customer, cart and draft reference must not carry over.
   *
   * Compares against the *previous* session's user, so it has to run before
   * setUserInfo() overwrites it. Prefers the SSO id; falls back to email for
   * sessions persisted before `User.id` existed.
   */
  private async resetIfUserChanged(newUser: {
    id: number;
    email: string;
  }): Promise<void> {
    // @ts-ignore — reading persisted state directly, before it is overwritten
    const prevUser: User | null = this.$store.state.user.user;
    if (!prevUser) return;
    const changed =
      prevUser.id !== undefined && newUser.id !== undefined
        ? prevUser.id !== newUser.id
        : !!prevUser.email && prevUser.email !== newUser.email;
    if (!changed) return;
    await this.resetOrderForUserChange();
    await this.setMultiplier(null);
  }

  async created(): Promise<void> {
    this.loader = this.$loading.show(
      { opacity: 1 },
      {
        before: this.$createElement("h1", "Authenticating...")
      }
    );

    const code = this.$route.query.code as string;

    // If already authenticated (e.g., back button or duplicate callback), go home
    // @ts-ignore
    const existingToken = this.$store.state.global.authToken;
    if (!code && existingToken) {
      this.loader.hide();
      this.$router.replace("/");
      return;
    }

    if (!code) {
      this.loader.hide();
      this.$router.replace("/login");
      return;
    }

    try {
      const data = await this.authService.exchangeCode(code);
      await this.setAuthToken(data.token);
      await this.setLoggedIn(true);

      // FUPOF-191: wipe the previous user's persisted order state. Must run
      // before setUserInfo() below replaces the user it compares against.
      await this.resetIfUserChanged(data.user);

      // Clear cached data from any previous session so checkAuth fetches fresh data
      await this.clearCustomers([]);
      await this.clearPriceLevels([]);

      // Store impersonation state (if present)
      await this.setImpersonation(data.impersonation || null);

      // Map SSO roles to app role
      const appRoles = data.roles[process.env.VUE_APP_SSO_APP_SLUG || "shade-quote"] || [];
      const role = appRoles.includes("super-admin") || appRoles.includes("admin")
        ? Roles.Admin
        : Roles.SalesRep;
      await this.setUserRole(role);

      // Store user info including rep IDs from SSO response
      await this.setUserInfo({
        id: data.user.id,
        first_name: data.user.name.split(" ")[0] || "",
        last_name: data.user.name.split(" ").slice(1).join(" ") || "",
        email: data.user.email,
        netsuite_id: data.user.netsuite_employee_id,
        effective_rep_ids: data.user.effective_rep_ids || [],
        role: role
      } as User);

      this.loader.hide();
      await this.$router.replace("/");
      EventBus.$emit("ssoAuthComplete");
    } catch (err) {
      console.error("SSO exchange failed:", err);
      this.loader.hide();
      // 403 = user doesn't have access to this app — redirect to central no-access page
      if (err.statusCode === 403 || err.response?.status === 403) {
        await this.setAuthToken(null);
        await this.setLoggedIn(false);
        const callback = encodeURIComponent(`${process.env.VUE_APP_URL}/auth/callback`);
        const appSlug = process.env.VUE_APP_SSO_APP_SLUG || "shade-quote";
        window.location.href = `${process.env.VUE_APP_API_LARAVEL_URL}/auth/no-access?redirect=${callback}&app=${appSlug}`;
        return;
      } else {
        this.$router.replace("/login");
      }
    }
  }
}
